import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useLayoutEffect, useState } from 'react';
import getRedirectQueryString from '../account/getRedirectQueryString';
import useIsLoggedIn from '../account/useIsLoggedIn';
import useLogout from '../account/useLogout';
import useAppSelector from '../app/redux/useAppSelector';
import Caret from '../components/Caret';
import Menu, { MenuItem, MenuSeparator } from '../components/Menu';
import PopOver from '../components/deprecated/PopOver';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess, UserType } from '../graphql/generated/graphql';
import {
  EmailSupportMenuItem,
  HelpDocumentationMenuItem,
  MessageSupportMenuItem,
  ProductUpdatesMenuItem,
} from '../help/HelpMenu';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import docky from '../images/docky.svg';
import TeamIcon from '../organization/TeamIcon';
import Presence from '../post/Presence';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import useSlug from '../utils/router/useSlug';
import ExternalSearchInput from './ExternalSearchInput';
import styles from './ExternalTopbar.module.scss';
import PostModeToggle from './PostModeToggle';
import ExternalBrandLogo from '../components/ExternalBrandLogo';

const POST_QUERY = gql(`
  query ExternalTopbarPost($id: ID!) {
    publicPost(id: $id) {
      id
      linkAccess
    }
  }
`);

interface BaseExternalTopbarProps {
  children: React.ReactNode;
}
const BaseExternalTopbar = ({ children }: BaseExternalTopbarProps) => {
  return (
    <header id="topbar" className={styles.externalTopbar} role="banner">
      {children}
    </header>
  );
};

const TeamIconLink = () => {
  return (
    <a href="/">
      <TeamIcon />
    </a>
  );
};

const TopbarMenu = () => {
  const redirectPath = useAppSelector(
    (state) => state.ui.externalTopbarLoginRedirectPath,
  );
  const isLoggedIn = useIsLoggedIn();
  const logout = useLogout({ preventRedirect: true });

  const [isPopOverOpen, setIsPopOverOpen] = useState(false);

  return (
    // @ts-expect-error
    <PopOver
      display="inline-block"
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      popOver={
        <Menu>
          <MessageSupportMenuItem wrap />
          <EmailSupportMenuItem wrap />
          <HelpDocumentationMenuItem wrap />
          <ProductUpdatesMenuItem wrap />
          <MenuSeparator />
          {isLoggedIn ? (
            <MenuItem icon={memberIcon} onClick={logout} wrap to="/login">
              Log out
            </MenuItem>
          ) : (
            <>
              <MenuItem
                icon={memberIcon}
                to={`/login${getRedirectQueryString(redirectPath)}`}
                wrap
              >
                Log in
              </MenuItem>
              <MenuSeparator fullWidth />
              <MenuItem
                to="https://go.slab.com/slab-register"
                target="_blank"
                wrap
                rel="noreferrer"
              >
                <div className={styles.trySlab}>
                  <div className={styles.trySlabLogo}>
                    <ExternalBrandLogo brand="slab" product="slab" />
                  </div>

                  <div>
                    <div className={styles.trySlabTitle}>Try Slab</div>
                    <div className={styles.trySlabDescription}>
                      Free up to 10 users
                    </div>
                  </div>
                </div>
              </MenuItem>
            </>
          )}
        </Menu>
      }
      placement="bottom-start"
      popOverPortal={document.body}
      responsivePlacement="bottom"
      trigger="click"
      open={isPopOverOpen}
      onOpenChange={setIsPopOverOpen}
    >
      <div
        className={classNames(styles.topbarMenu, {
          [styles.isPopOverOpen]: isPopOverOpen,
        })}
      >
        <img className={styles.logo} src={docky} width={24} alt="Slab" />
        <div className={styles.caret}>
          <Caret />
        </div>
      </div>
    </PopOver>
  );
};

const PostToggleButton = () => {
  const [postId] = useSlug('postSlug');
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isEditorReady = useAppSelector((state) => state.editor.version >= 0);

  const { data: postData } = useQuery(POST_QUERY, {
    variables: { id: postId || '' },
    skip: !postId,
  });

  if (
    postData?.publicPost.linkAccess !== PostLinkAccess.PublicEdit ||
    !isEditorReady
  ) {
    return null;
  }

  return (
    <>
      {!readOnly && currentUser.type === UserType.Visitor && (
        <div className={styles.editingNote}>
          You are editing as {currentUser.name}
        </div>
      )}
      {!readOnly && <Presence />}
      <PostModeToggle />
      <div className={styles.modeToggleSeparator} />
    </>
  );
};

const ExternalTopbarWithSearch = () => {
  const isSmall = useIsSize(Sizes.SM);
  const [isShowingSearch, setIsShowingSearch] = useState(!isSmall);

  useLayoutEffect(() => {
    setIsShowingSearch(!isSmall);
  }, [isSmall]);

  return (
    <BaseExternalTopbar>
      <div className={styles.left}>
        <TeamIconLink />
        <ExternalSearchInput
          isShowingSearch={isShowingSearch}
          setIsShowingSearch={setIsShowingSearch}
        />
      </div>

      {(!isSmall || !isShowingSearch) && (
        <div className={styles.actions}>
          <PostToggleButton />
          <TopbarMenu />
        </div>
      )}
    </BaseExternalTopbar>
  );
};

const ExternalTopbar = () => {
  return (
    <BaseExternalTopbar>
      <TeamIconLink />
      <div className={styles.actions}>
        <PostToggleButton />
        <TopbarMenu />
      </div>
    </BaseExternalTopbar>
  );
};

ExternalTopbar.WithSearch = ExternalTopbarWithSearch;

export default ExternalTopbar;
